import React, { useState } from 'react';
import type { DateFormat } from '@shootout-comps/types';

interface DateTimeFieldProps {
  value: Date | null;
  onChange: (d: Date | null) => void;
  dateFormat: DateFormat;
  placeholder?: string;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// Renders the selected value in the user's chosen day/month/year order — the whole point of this
// component is that picking a day is always a calendar click, never typed digits, so this text is
// purely informational, not something the user has to parse an ambiguous order out of.
function formatSummary(d: Date, fmt: DateFormat): string {
  const dd = pad2(d.getDate()), mm = pad2(d.getMonth() + 1), yyyy = String(d.getFullYear());
  const datePart = fmt === 'DMY' ? `${dd}/${mm}/${yyyy}` : fmt === 'MDY' ? `${mm}/${dd}/${yyyy}` : `${yyyy}-${mm}-${dd}`;
  return `${datePart} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Monday-first offset (0 = Monday) for a JS Date whose getDay() is 0=Sunday..6=Saturday.
function mondayOffset(getDay: number): number {
  return (getDay + 6) % 7;
}

export default function DateTimeField({ value, onChange, dateFormat, placeholder = 'Pick date & time' }: DateTimeFieldProps) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<Date>(() => {
    const base = value ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = mondayOffset(firstOfMonth.getDay());
  const totalCells = Math.ceil((leadingBlanks + daysInMonth) / 7) * 7;
  const today = new Date();
  const monthLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const selectDay = (day: number) => {
    const next = new Date(year, month, day);
    if (value) next.setHours(value.getHours(), value.getMinutes(), 0, 0);
    else next.setHours(today.getHours(), today.getMinutes(), 0, 0);
    onChange(next);
  };

  const changeTime = (timeStr: string) => {
    if (!timeStr) return;
    const [h, m] = timeStr.split(':').map(Number);
    const base = value ?? today;
    const next = new Date(base);
    next.setHours(h, m, 0, 0);
    onChange(next);
  };

  const inputSty: React.CSSProperties = {
    width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px', padding: '0.5rem 0.6rem', color: value ? 'var(--chalk)' : 'var(--chalk-dim)', fontSize: '0.82rem', cursor: 'pointer',
  };

  const navBtn: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px',
    color: 'var(--chalk)', width: '28px', height: '28px', fontSize: '0.85rem', cursor: 'pointer',
  };

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
      <button type="button" style={inputSty} onClick={() => setOpen(o => !o)}>
        {value ? formatSummary(value, dateFormat) : placeholder}
      </button>

      {open && (
        <>
          {/* Click-away backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 190 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200, width: '260px',
              background: 'var(--rail)', border: '1px solid var(--gold)', borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)', padding: '0.75rem',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <button type="button" style={navBtn} onClick={() => setCursor(new Date(year, month - 1, 1))}>‹</button>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--gold)' }}>{monthLabel}</span>
              <button type="button" style={navBtn} onClick={() => setCursor(new Date(year, month + 1, 1))}>›</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
              {WEEKDAY_LABELS.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.58rem', color: 'var(--chalk-dim)', padding: '0.15rem 0' }}>{d}</div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '0.6rem' }}>
              {Array.from({ length: totalCells }, (_, i) => {
                const dayNum = i - leadingBlanks + 1;
                if (dayNum < 1 || dayNum > daysInMonth) return <div key={i} />;
                const isSelected = !!value && value.getFullYear() === year && value.getMonth() === month && value.getDate() === dayNum;
                const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === dayNum;
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => selectDay(dayNum)}
                    style={{
                      aspectRatio: '1', fontSize: '0.72rem', borderRadius: '5px', cursor: 'pointer',
                      background: isSelected ? 'var(--gold)' : 'transparent',
                      color: isSelected ? 'var(--baize-dark)' : 'var(--chalk)',
                      border: isToday && !isSelected ? '1px solid rgba(201,168,76,0.6)' : '1px solid transparent',
                      fontWeight: isSelected ? 700 : 400,
                    }}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input
                type="time"
                value={value ? `${pad2(value.getHours())}:${pad2(value.getMinutes())}` : ''}
                onChange={e => changeTime(e.target.value)}
                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.35rem 0.5rem', color: 'var(--chalk)', fontSize: '0.8rem', outline: 'none', colorScheme: 'dark' }}
              />
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
